<form [formGroup]="form" class="arm" (ngSubmit)="save()">
  <header class="arm-head">
    <div>
      <h2 class="arm-title">{{ isEdit ? 'Edit reservation' : 'New reservation' }}</h2>
      <p class="arm-sub">Pick the party and date, then a time the floor can seat.</p>
    </div>
    <button type="button" class="arm-close" (click)="cancel()" aria-label="Close">
      <i class="bx bx-x" aria-hidden="true"></i>
    </button>
  </header>

  <div class="arm-body">
    <section class="arm-col">
      <label class="arm-label" for="rsvDate">Date</label>
      <input
        id="rsvDate"
        type="date"
        class="arm-input"
        formControlName="date"
        appPickerOnlyDate
        [min]="today"
        [max]="maxDate"
      />

      <label class="arm-label">Party size</label>
      <div class="arm-party">
        <button type="button" class="arm-step" (click)="bumpParty(-1)" aria-label="Fewer guests">
          −
        </button>
        <input
          type="number"
          class="arm-input arm-input--num"
          formControlName="partySize"
          min="1"
          [max]="maxStaffParty"
          aria-label="Party size"
        />
        <button type="button" class="arm-step" (click)="bumpParty(1)" aria-label="More guests">
          +
        </button>
        <span class="arm-hint">max {{ maxParty }} online</span>
      </div>
      @if (needsApproval) {
        <p class="arm-note">
          <i class="bx bx-info-circle" aria-hidden="true"></i> Parties this size are saved as a
          request to confirm.
        </p>
      }

      <label class="arm-label" for="rsvName">Guest name</label>
      <input
        id="rsvName"
        type="text"
        class="arm-input"
        formControlName="name"
        placeholder="e.g. Priya Natarajan"
        autocomplete="off"
        [class.arm-input--error]="form.get('name')?.invalid && form.get('name')?.touched"
      />
      @if (form.get('name')?.touched && form.get('name')?.hasError('noLetters')) {
        <p class="arm-field-error">A name needs letters, not only numbers.</p>
      }

      <div class="arm-two">
        <div>
          <label class="arm-label" for="rsvPhone">Phone</label>
          <input
            id="rsvPhone"
            type="tel"
            class="arm-input"
            formControlName="phone"
            placeholder="(555) 123-4567"
            autocomplete="off"
            (input)="onPhoneInput($event)"
            [class.arm-input--error]="
              (form.get('phone')?.invalid && form.get('phone')?.touched) || showContactError
            "
          />
          @if (form.get('phone')?.invalid && form.get('phone')?.touched) {
            <p class="arm-field-error">Enter a phone number as (555) 123-4567.</p>
          }
        </div>
        <div>
          <label class="arm-label" for="rsvEmail">Email</label>
          <input
            id="rsvEmail"
            type="email"
            class="arm-input"
            formControlName="email"
            placeholder="guest@example.com"
            [class.arm-input--error]="
              (form.get('email')?.invalid && form.get('email')?.touched) || showContactError
            "
          />
          @if (form.get('email')?.invalid && form.get('email')?.touched) {
            <p class="arm-field-error">Enter an email such as guest&#64;example.com.</p>
          }
        </div>
      </div>
      <!-- Found by the phone or email above, before saving (FNF-442). -->
      @if (returning; as guest) {
        <div class="arm-returning" role="status" data-testid="returning-guest">
          <i class="bx bx-user-check arm-returning-icon" aria-hidden="true"></i>
          <div class="arm-returning-body">
            <span class="arm-returning-title">
              Returning guest
              @for (tag of guest.standingNotes.tags; track tag) {
                <span class="arm-returning-tag">{{ tag }}</span>
              }
            </span>
            <span class="arm-hint">{{ returningSummary }}</span>
            @if (guest.existing.length) {
              <span class="arm-existing" data-testid="existing-reservation">
                <i class="bx bx-calendar-check" aria-hidden="true"></i> Already has a reservation
              </span>
              @for (booking of guest.existing; track booking.reservationId) {
                <span class="arm-existing-line">{{ existingLabel(booking) }}</span>
              }
            }
            @if (guest.standingNotes.allergies) {
              <span class="arm-returning-allergy">
                <i class="bx bx-error" aria-hidden="true"></i> Allergy:
                {{ guest.standingNotes.allergies }}
              </span>
            }
            @if (guest.standingNotes.preferences) {
              <span class="arm-hint">Prefers: {{ guest.standingNotes.preferences }}</span>
            }
          </div>
        </div>
      } @else if (firstTime) {
        <span class="arm-first-time" role="status" data-testid="first-time-guest">
          <i class="bx bx-user-plus" aria-hidden="true"></i> First-Time Customer
        </span>
      }
      @if (showContactError) {
        <p class="arm-field-error" role="alert">
          Add a phone number or an email, so the guest can be reached.
        </p>
      } @else {
        <p class="arm-hint arm-contact-hint">A phone number or an email is needed; both is fine.</p>
      }
      <!-- SMS opt-in, worded as registered for the Reservations Campaign. -->
      <p class="arm-hint arm-consent">
        {{ smsConsent.text }}
        <a [href]="smsConsent.termsUrl" target="_blank" rel="noopener">Terms</a> ·
        <a [href]="smsConsent.privacyUrl" target="_blank" rel="noopener">Privacy</a>
      </p>

      <label class="arm-label">Occasion</label>
      <div class="arm-chips">
        @for (o of occasions; track o) {
          <button
            type="button"
            class="arm-chip"
            [class.arm-chip--on]="form.get('occasion')?.value === o"
            (click)="setOccasion(o)"
          >
            {{ o }}
          </button>
        }
      </div>

      <label class="arm-label" for="rsvNotes">Notes for the floor</label>
      <textarea
        id="rsvNotes"
        class="arm-input arm-textarea"
        formControlName="notes"
        rows="2"
        placeholder="This booking only, e.g. arriving at 7:15"
      ></textarea>

      <!-- Kept on the guest for every visit (FNF-442), apart from the note above. -->
      <span class="arm-label">Guest notes · kept for every visit</span>
      <div class="arm-two">
        <div>
          <label class="arm-label" for="rsvAllergies">Allergies</label>
          <input
            id="rsvAllergies"
            class="arm-input"
            formControlName="allergies"
            maxlength="200"
            placeholder="e.g. Tree nuts"
            autocomplete="off"
          />
        </div>
        <div>
          <label class="arm-label" for="rsvPreferences">Preferences</label>
          <input
            id="rsvPreferences"
            class="arm-input"
            formControlName="preferences"
            maxlength="300"
            placeholder="e.g. Window table, high chair"
            autocomplete="off"
          />
        </div>
      </div>
      <p class="arm-hint">
        @if (notesFromHistory) {
          Filled in from this guest's earlier visits. Change them here if they've changed.
        } @else {
          Saved on the guest, so their next booking shows them without retyping.
        }
      </p>

      @if (!isEdit && !needsApproval) {
        <label class="arm-check">
          <input type="checkbox" formControlName="confirmNow" />
          Confirm immediately (uncheck to hold as a request)
        </label>
      }
    </section>

    <section class="arm-col arm-col--slots">
      <div class="arm-slots-head">
        <span class="arm-label">Available times</span>
        @if (loadingSlots) {
          <span class="arm-hint">Checking tables…</span>
        } @else if (selectedSlot) {
          <span class="arm-hint"
            >{{ selectedSlot.label }} · table
            {{ (selectedSlot.tableNames | tableNames) || 'auto' }}</span
          >
        }
      </div>
      @if (zoneNote) {
        <p class="arm-zone" role="note">
          <i class="bx bx-time" aria-hidden="true"></i> {{ zoneNote }}
        </p>
      }
      @switch (emptyReason) {
        @case ('unset') {
          <div class="arm-empty" role="status">
            <i class="bx bx-time-five" aria-hidden="true"></i>
            <div>
              <p class="arm-empty-title">Opening hours aren't set up yet</p>
              <p class="arm-empty-body">
                Times are built from your serving hours, so there is nothing to book until they're
                added.
              </p>
              <button type="button" class="arm-empty-action" (click)="goToServingHours()">
                Set up serving hours
              </button>
            </div>
          </div>
        }
        @case ('closed') {
          <div class="arm-empty" role="status">
            <i class="bx bx-calendar-x" aria-hidden="true"></i>
            <div>
              <p class="arm-empty-title">Closed on {{ weekdayName }}s</p>
              <p class="arm-empty-body">
                Your serving hours don't cover this day. Pick another date, or add hours for
                {{ weekdayName }}.
              </p>
              <button type="button" class="arm-empty-action" (click)="goToServingHours()">
                Edit serving hours
              </button>
            </div>
          </div>
        }
        @case ('closed_date') {
          <div class="arm-empty" role="status" data-testid="closed-date">
            <i class="bx bx-calendar-x" aria-hidden="true"></i>
            <div>
              <p class="arm-empty-title">
                Closed on {{ dateLabel }}{{ dayNote ? ' (' + dayNote + ')' : '' }}
              </p>
              <p class="arm-empty-body">
                The restaurant is closed on this date, so it takes no bookings. Pick another date.
              </p>
              <button type="button" class="arm-empty-action" (click)="goToClosedDates()">
                See closed dates
              </button>
            </div>
          </div>
        }
        @case ('full') {
          <div class="arm-empty" role="status">
            <i class="bx bx-info-circle" aria-hidden="true"></i>
            <div>
              <p class="arm-empty-title">No times free for this party</p>
              <p class="arm-empty-body">
                Every time is taken or has passed. Try another date or a smaller party.
              </p>
            </div>
          </div>
        }
        @case ('error') {
          <div class="arm-empty arm-empty--error" role="alert">
            <i class="bx bx-error-circle" aria-hidden="true"></i>
            <div>
              <p class="arm-empty-title">Couldn't load times</p>
              <p class="arm-empty-body">Check your connection and try again.</p>
              <button type="button" class="arm-empty-action" (click)="retrySlots()">Retry</button>
            </div>
          </div>
        }
      }
      @for (period of periods; track period.name) {
        <div class="arm-period">
          <span class="arm-period-name">{{ period.name }}</span>
          <div class="arm-slot-grid">
            @for (slot of slotsFor(period); track slot.startAt) {
              <!-- The tooltip sits on a wrapper: a disabled button gets no
                   mouse events, so it could never say why it is off. -->
              <span class="arm-slot-wrap" [matTooltip]="slotTooltip(slot)">
                <button
                  type="button"
                  class="arm-slot"
                  [class.arm-slot--on]="slot.startAt === selectedStartAt"
                  [class.arm-slot--off]="!slot.available"
                  [disabled]="!slot.available && slot.startAt !== data.reservation?.startAt"
                  [attr.aria-label]="slot.label + ', ' + slotTooltip(slot)"
                  (click)="pick(slot)"
                >
                  {{ slot.label }}
                </button>
              </span>
            }
            @if (!slotsFor(period).length && !loadingSlots) {
              <span class="arm-hint">No slots in this service.</span>
            }
          </div>
        </div>
      }
      @if (periods.length) {
        <p class="arm-legend">
          <span class="arm-legend-dot"></span> bookable
          <span class="arm-legend-dot arm-legend-dot--on"></span> selected
          <span class="arm-legend-dot arm-legend-dot--off"></span> full or already passed
        </p>
      }
    </section>
  </div>

  <footer class="arm-foot">
    <button type="button" class="arm-cancel" (click)="cancel()">Cancel</button>
    <button type="submit" class="arm-save" [disabled]="!canSave">
      {{
        isEdit
          ? 'Save changes'
          : needsApproval || !form.get('confirmNow')?.value
            ? 'Add request'
            : 'Confirm reservation'
      }}
    </button>
  </footer>
</form>
